import type { Entry } from "@/shared/types/entries";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import type { AddPayload } from "@/v2/hooks/actions/add";
import type { EditPayload } from "@/v2/hooks/actions/edit";
import type { EntryIdInput } from "@/v2/hooks/actions/entryId";
import { parseEntryDate } from "@/v2/lib/dates";
import { secondsToBillingUnits } from "@/v2/lib/time";

export interface AddEntryPayloadOptions {
	timekeeperId?: string | null;
}

export interface EditEntryPayloadOptions {
	timekeeperId?: string | null;
}

export interface TimerEntryDefaultOptions {
	date: Date;
	timerDurationSeconds: number | null;
	carryForward: Partial<TimeEntryFormValues> | null;
}

export function createAddEntryPayload(
	values: TimeEntryFormValues,
	date: string,
	options?: AddEntryPayloadOptions,
): AddPayload {
	const payload: AddPayload = {
		id: 0,
		date,
		matter: values.matter,
		matter_id: values.matter_id,
		description: values.description,
		duration: values.duration,
		task: values.task,
		activity: values.activity ?? "",
		timekeeperId: options?.timekeeperId ?? null,
	};

	return payload;
}

export function createEditEntryPayload(
	entryId: EntryIdInput,
	values: TimeEntryFormValues,
	date: string,
	options?: EditEntryPayloadOptions,
): EditPayload {
	return {
		id: entryId,
		date,
		matter: values.matter,
		matter_id: values.matter_id,
		description: values.description,
		duration: values.duration,
		task: values.task,
		activity: values.activity ?? "",
		timekeeperId: options?.timekeeperId ?? null,
	};
}

export function createEntryFormDefaults(
	entry: Entry,
): Partial<TimeEntryFormValues> {
	return {
		date: parseEntryDate(entry.date),
		matter_id: entry.matter?.id ?? "",
		matter: entry.matter?.code ?? null,
		task: entry.codes.taskCode ?? "",
		activity: entry.codes.activityCode ?? "",
		duration: entry.units ?? 0,
		description: entry.description ?? "",
	};
}

export function createCarryForwardValues(
	values: TimeEntryFormValues,
): Partial<TimeEntryFormValues> {
	return {
		date: values.date,
		matter_id: values.matter_id,
		matter: values.matter,
		task: values.task,
		activity: values.activity,
		duration: values.duration,
		description: values.description,
	};
}

export function createTimerEntryDefaults({
	date,
	timerDurationSeconds,
	carryForward,
}: TimerEntryDefaultOptions): Partial<TimeEntryFormValues> {
	const defaults: Partial<TimeEntryFormValues> = { date };

	if (timerDurationSeconds !== null) {
		defaults.duration = secondsToBillingUnits(timerDurationSeconds);
	}

	return carryForward ? { ...defaults, ...carryForward } : defaults;
}
